import { useEffect, useState } from 'react';

import { KassenApi } from '../api';
import {
  ersparnisCent,
  KundenanzeigeEmpfaenger,
  kundenanzeigeKanal,
  LEERER_STAND,
  sichtbareZeilen,
  type KundenanzeigeStand,
  type Zahlungsanzeige,
} from '../kundenanzeige';
import { anzeigeKasse } from '../kundenanzeige-fenster';
import { formatiereEuro } from '../warenkorb';

/** Zeilenhöhe der Artikelliste in vh (siehe `.kundenanzeige-liste li` in styles.css). */
const ZEILE_VH = 8;
/** Anteil der Fensterhöhe für die Artikelliste (ohne Zusatzzeilen). */
const LISTE_ANTEIL = 0.6;

function apiBasisUrl(): string {
  const konfiguriert: unknown = import.meta.env.VITE_API_URL;
  return typeof konfiguriert === 'string' && konfiguriert !== '' ? konfiguriert : '/api';
}

const ZAHLUNG_TEXT: Record<Exclude<Zahlungsanzeige, 'keine'>, string> = {
  bar: 'Barzahlung',
  karte: 'Bitte zahlen Sie am Kartenterminal',
  beleg: 'Zahlung erfolgreich – Beleg wird erstellt …',
  pruefung: 'Zahlung wird geprüft – einen Moment bitte',
  abgelehnt: 'Kartenzahlung abgelehnt',
  abgebrochen: 'Kartenzahlung abgebrochen',
};

/** Zeilen, die in die Liste passen; jede Zusatzzeile unter der Liste kostet eine. */
function maxZeilen(zusatzzeilen: number): number {
  return Math.max(3, Math.floor((LISTE_ANTEIL * 100) / ZEILE_VH) - zusatzzeilen);
}

/**
 * Kundenanzeige (zweites Fenster, kundenseitiger Bildschirm, ausgelegt für
 * 1024 × 768): empfängt den Bon der eigenen Kasse über den BroadcastChannel und
 * zeigt Positionen mit Menge, Einzelpreis und Rabatt, Summe, Zahlungsstand und
 * nach dem Kassieren Gezahlt/Rückgeld. Keine Bedienung, keine Belegerstellung.
 * Doppelklick schaltet Vollbild.
 */
export function Kundenanzeige() {
  const parameter = new URLSearchParams(window.location.search);
  const [bindung] = useState(() =>
    anzeigeKasse(parameter.get('kasse'), localStorage.getItem('bilanx.kassenId') ?? ''),
  );
  const kassenId = 'kassenId' in bindung ? bindung.kassenId : null;
  const [stand, setStand] = useState<KundenanzeigeStand>(LEERER_STAND);
  const [unternehmer, setUnternehmer] = useState<string>('');
  const [vollbild, setVollbild] = useState(document.fullscreenElement !== null);

  useEffect(() => {
    if (kassenId === null) {
      return;
    }
    const api = new KassenApi({
      basisUrl: apiBasisUrl(),
      token: () => localStorage.getItem('bilanx.kassenschluessel'),
    });
    api
      .status(kassenId)
      .then((s) => {
        setUnternehmer(s.unternehmer?.name ?? '');
      })
      .catch(() => undefined);
  }, [kassenId]);

  useEffect(() => {
    if (kassenId === null || typeof BroadcastChannel !== 'function') {
      return;
    }
    const e = new KundenanzeigeEmpfaenger(kassenId);
    const kanal = new BroadcastChannel(kundenanzeigeKanal(kassenId));
    const anfragen = (): void => {
      kanal.postMessage({ typ: 'anfrage', kassenId });
    };
    kanal.onmessage = (ereignis: MessageEvent<unknown>) => {
      const ergebnis = e.nachricht(ereignis.data, Date.now());
      if (ergebnis.geaendert) {
        setStand(e.stand);
      }
      if (ergebnis.anfragen) {
        anfragen();
      }
    };
    // Neu geöffnet (auch mitten im Verkauf): laufenden Bon von der Kasse holen.
    anfragen();
    const pruefen = (): void => {
      if (e.tick(Date.now())) {
        setStand(e.stand);
      }
    };
    const takt = setInterval(pruefen, 1_000);
    // Gedrosselte Zeitgeber (verdecktes Fenster): beim Wiedersichtbarwerden sofort prüfen.
    document.addEventListener('visibilitychange', pruefen);
    window.addEventListener('pageshow', pruefen);
    return () => {
      clearInterval(takt);
      document.removeEventListener('visibilitychange', pruefen);
      window.removeEventListener('pageshow', pruefen);
      kanal.onmessage = null;
      kanal.close();
    };
  }, [kassenId]);

  // Alter Aufruf ohne ?kasse= (Lesezeichen): einmalig fest an die jetzige Kasse binden,
  // damit ein späterer Kassenwechsel auf dem Gerät nicht still eine andere Kasse zeigt.
  useEffect(() => {
    if (kassenId !== null && new URLSearchParams(window.location.search).get('kasse') === null) {
      const url = new URL(window.location.href);
      url.searchParams.set('kasse', kassenId);
      window.history.replaceState(null, '', url);
    }
  }, [kassenId]);

  // Kasse auf dem Gerät gewechselt: neu laden, damit die Anzeige der neuen Kasse folgt
  // (bzw. bei fester ?kasse= den Fehler zeigt) – nie den Bon der falschen Kasse.
  useEffect(() => {
    const beiSpeicher = (ev: StorageEvent): void => {
      if (ev.key === 'bilanx.kassenId' || ev.key === null) {
        window.location.reload();
      }
    };
    window.addEventListener('storage', beiSpeicher);
    const beiVollbild = (): void => {
      setVollbild(document.fullscreenElement !== null);
    };
    document.addEventListener('fullscreenchange', beiVollbild);
    return () => {
      window.removeEventListener('storage', beiSpeicher);
      document.removeEventListener('fullscreenchange', beiVollbild);
    };
  }, []);

  function vollbildUmschalten(): void {
    if (document.fullscreenElement === null) {
      document.documentElement.requestFullscreen().catch(() => undefined);
    } else {
      document.exitFullscreen().catch(() => undefined);
    }
  }

  if ('fehler' in bindung) {
    return (
      <div className="kundenanzeige">
        <div className="kundenanzeige-mitte">
          <h1>Kundenanzeige nicht verbunden</h1>
          <p className="kundenanzeige-hinweis">{bindung.fehler}</p>
        </div>
      </div>
    );
  }

  const ersparnis = ersparnisCent(stand.positionen);
  const zusatz =
    (ersparnis > 0 ? 1 : 0) + (stand.gutscheinCent > 0 ? 1 : 0) + (stand.zahlung === null ? 0 : 1);
  const { zeilen, ausgeblendet } = sichtbareZeilen(stand.positionen, maxZeilen(zusatz));

  return (
    <div className="kundenanzeige" onDoubleClick={vollbildUmschalten}>
      <header>
        <span>{unternehmer}</span>
        <span className="kundenanzeige-kasse">Kasse {bindung.kassenId}</span>
      </header>
      {stand.modus === 'willkommen' && (
        <div className="kundenanzeige-mitte">
          <h1>Willkommen</h1>
        </div>
      )}
      {stand.modus === 'bon' && (
        <>
          <ul className="kundenanzeige-liste">
            {ausgeblendet > 0 && (
              <li className="kundenanzeige-weitere">
                … {ausgeblendet} weitere {ausgeblendet === 1 ? 'Position' : 'Positionen'}
              </li>
            )}
            {zeilen.map((z, i) => (
              <li
                key={`${String(ausgeblendet + i)}-${z.bezeichnung}`}
                className={[z.istRabatt ? 'rabatt' : '', i === zeilen.length - 1 ? 'neu' : '']
                  .filter((k) => k !== '')
                  .join(' ')}
              >
                <span className="kundenanzeige-bezeichnung">{z.bezeichnung}</span>
                <span className="kundenanzeige-betrag">{formatiereEuro(z.bruttoCent)} €</span>
                {!z.istRabatt && (z.kg || z.menge !== 1 || z.rabattProzent !== null) && (
                  <span className="kundenanzeige-detail">
                    {z.mengeText}
                    {z.rabattProzent !== null && ` − ${String(z.rabattProzent)} % Rabatt`}
                  </span>
                )}
              </li>
            ))}
          </ul>
          {ersparnis > 0 && (
            <div className="kundenanzeige-zeile ersparnis">
              <span>Sie sparen</span>
              <span>{formatiereEuro(ersparnis)} €</span>
            </div>
          )}
          {stand.gutscheinCent > 0 && (
            <div className="kundenanzeige-zeile">
              <span>Gutschein</span>
              <span>−{formatiereEuro(stand.gutscheinCent)} €</span>
            </div>
          )}
          <div className="kundenanzeige-summe">
            <span>{stand.gutscheinCent > 0 ? 'Zu zahlen' : 'Summe'}</span>
            <span>{formatiereEuro(stand.summeCent)} €</span>
          </div>
          {stand.zahlung !== null && (
            <div
              className={`kundenanzeige-zahlung ${stand.zahlung.status}`}
              role="status"
              aria-live="polite"
            >
              {ZAHLUNG_TEXT[stand.zahlung.status]}
              {stand.zahlung.status === 'karte' &&
                ` · ${formatiereEuro(stand.zahlung.betragCent)} €`}
            </div>
          )}
        </>
      )}
      {stand.modus === 'bezahlt' && stand.bezahlt !== null && (
        <div className="kundenanzeige-mitte">
          <h1>Vielen Dank!</h1>
          <div className="kundenanzeige-summe">
            <span>Summe</span>
            <span>{formatiereEuro(stand.bezahlt.summeCent)} €</span>
          </div>
          {stand.bezahlt.gutscheinCent > 0 && (
            <div className="kundenanzeige-zeile">
              <span>Gutschein</span>
              <span>−{formatiereEuro(stand.bezahlt.gutscheinCent)} €</span>
            </div>
          )}
          {stand.bezahlt.zahlungsart === 'karte' && (
            <div className="kundenanzeige-zeile">
              <span>Karte</span>
              <span>
                {formatiereEuro(stand.bezahlt.summeCent - stand.bezahlt.gutscheinCent)} € bezahlt
              </span>
            </div>
          )}
          {stand.bezahlt.gegebenCent !== null && stand.bezahlt.rueckgeldCent !== null && (
            <>
              <div className="kundenanzeige-zeile">
                <span>Gegeben</span>
                <span>{formatiereEuro(stand.bezahlt.gegebenCent)} €</span>
              </div>
              <div className="kundenanzeige-zeile gross">
                <span>Rückgeld</span>
                <span>{formatiereEuro(stand.bezahlt.rueckgeldCent)} €</span>
              </div>
            </>
          )}
          {stand.bezahlt.trinkgeldCent > 0 && (
            <div className="kundenanzeige-zeile">
              <span>Trinkgeld</span>
              <span>{formatiereEuro(stand.bezahlt.trinkgeldCent)} €</span>
            </div>
          )}
        </div>
      )}
      {!vollbild && <footer className="kundenanzeige-fuss">Doppelklick: Vollbild</footer>}
    </div>
  );
}
